import { getDojoActivityStatus } from './dojoActivityStatus';
import { countsTowardDojoDayTotals } from './dojoActivityDayProjection';

const memberId = user => user?._id || user?.user_id || user?.id;

function uniqueActivities(activities, type, ownerId) {
  const unique = new Map();
  activities.forEach(activity => {
    if (!activity?._id || activity.is_template || activity.type !== type
        || String(activity.user_id) !== String(ownerId)) return;
    unique.set(String(activity._id), activity);
  });
  return [...unique.values()];
}

function workoutDayScore(rows, now) {
  const sources = new Map();
  rows.forEach(row => {
    if (!countsTowardDojoDayTotals(row) || !getDojoActivityStatus(row, now).countable) return;
    const source = row._dojoReplacementReference?.relativeDays === 0
      ? row._dojoReplacementReference.activity : row;
    sources.set(String(source._id), source);
  });
  if (!sources.size) return null;
  let total = 0;
  for (const source of sources.values()) {
    const score = source.score_summary?.total_score;
    if (typeof score !== 'number' || !Number.isFinite(score) || score < 0) return null;
    total += score;
  }
  return Number.isFinite(total) ? total : null;
}

function workoutSetProgress(rows) {
  const totals = rows.reduce((sum, activity) => {
    const source = activity?._dojoReplacementReference?.relativeDays === 0
      ? activity._dojoReplacementReference.activity
      : activity;
    const exercises = Array.isArray(source?.data?.exercises) ? source.data.exercises : [];
    const sets = exercises
      .filter(exercise => exercise?.status !== 'removed')
      .flatMap(exercise =>
        Array.isArray(exercise?.sets) ? exercise.sets : []
      );
    if (sets.length) {
      sum.completed += sets.filter(set => set?.complete === true).length;
      sum.planned += sets.length;
      return sum;
    }
    const completed = Number(source?.workout_progress?.completed_sets);
    const planned = Number(source?.workout_progress?.planned_sets);
    if (Number.isInteger(completed) && Number.isInteger(planned)
        && planned > 0 && completed >= 0 && completed <= planned) {
      sum.completed += completed;
      sum.planned += planned;
    }
    return sum;
  }, { completed: 0, planned: 0 });
  if (!totals.planned) return null;
  const completedSets = totals.completed;
  const plannedSets = totals.planned;
  return {
    completedSets,
    plannedSets,
    progress: Math.round((completedSets / plannedSets) * 100),
    label: `${completedSets}/${plannedSets} sets`,
  };
}

const EXPIRED_WORKOUT_STATUSES = new Set(['missed', 'expired']);
const OPEN_WORKOUT_STATUSES = new Set(['pending', 'ongoing', 'in_progress', 'started']);

function workoutIsExpired(activity, now) {
  if (activity?._dojoReplacementReference?.activity
      && activity._dojoReplacementReference.relativeDays === 0) return false;
  const status = String(activity?.status || '').toLowerCase();
  if (EXPIRED_WORKOUT_STATUSES.has(status)) return true;
  if (!OPEN_WORKOUT_STATUSES.has(status)) return false;
  const dueTime = activity?.due_at ? new Date(activity.due_at).getTime() : NaN;
  const nowTime = new Date(now).getTime();
  return Number.isFinite(dueTime) && Number.isFinite(nowTime) && dueTime < nowTime;
}

function presentation(rows, isSelf, now, type) {
  if (!rows.length) {
    return {
      status: 'unavailable',
      label: isSelf ? 'None' : 'Unknown',
      symbol: isSelf ? '—' : '?',
      completed: 0,
      total: 0,
    };
  }

  const states = rows.map(activity => getDojoActivityStatus(activity, now));
  const countable = states.filter(state => state.countable);
  if (!countable.length) {
    if (states.some(state => state.status === 'replaced')) {
      return { status: 'replaced', label: 'Offset', symbol: '↔', completed: 0, total: 0 };
    }
    return { status: 'rest', label: 'Rest', symbol: '☾', completed: 0, total: 0 };
  }

  const completed = countable.filter(state => state.completedForDay).length;
  const total = countable.length;
  const setProgress = type === 'workout'
    ? workoutSetProgress(rows.filter((row, index) => states[index].countable))
    : null;
  const label = setProgress?.label || `${completed}/${total} done`;
  // Ring progress is independent of missed activity warnings: earned work and
  // a genuinely started session remain blue even between workouts/after expiry.
  const workoutProgress = type === 'workout' ? {
    hasWorkoutProgress: completed > 0 || countable.some(state => state.status === 'live')
      || (setProgress?.completedSets || 0) > 0,
  } : {};
  const withProgress = value => ({ ...value, ...(setProgress || {}), ...workoutProgress });
  if (type === 'workout' && rows.some(activity => workoutIsExpired(activity, now))) {
    return withProgress({ status: 'missed', label, symbol: '!', completed, total });
  }
  if (completed === total) return withProgress({ status: 'done', label, symbol: '✓', completed, total });
  if (countable.some(state => state.status === 'live')) {
    return withProgress({ status: 'active', label, symbol: '●', completed, total });
  }
  if (type !== 'workout' && countable.some(state => state.status === 'missed')) {
    return withProgress({ status: 'missed', label, symbol: '!', completed, total });
  }
  return withProgress({ status: completed > 0 ? 'partial' : 'pending', label, symbol: '…', completed, total });
}

/**
 * Summarize viewer-authorized activity rows for a single day/type.
 * Personal rows remain authoritative for self; peer rows come only from the
 * already privacy-filtered Dojo feed supplied by the caller.
 */
export function buildDojoActivityTypeMembers({
  users = [], activities = [], personalActivities = [], type, currentUserId, now = new Date(),
}) {
  const roster = new Map();
  users.forEach(user => {
    const id = memberId(user);
    if (id != null) roster.set(String(id), user);
  });

  return [...roster].map(([id, user]) => {
    const isSelf = id === String(currentUserId);
    const rows = uniqueActivities(isSelf ? personalActivities : activities, type, id);
    return { id, user, isSelf, activities: rows, ...presentation(rows, isSelf, now, type),
      ...(type === 'workout' ? { workoutScore: workoutDayScore(rows, now) } : {}),
    };
  }).sort((a, b) => Number(b.isSelf) - Number(a.isSelf));
}

export function resolveDojoActivityTypeMember(members = [], selection, scope) {
  return members.find(member => selection?.scope === scope && member.id === selection.id)
    || members.find(member => member.isSelf)
    || members[0];
}
